import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  PlusCircle, Search, Download, Mail, Ban, RefreshCw, Upload,
  CalendarClock, Activity, TrendingUp, AlertCircle, Eye, ShieldBan, Trash2, RefreshCw as RenewIcon
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { getStatusBadgeStyle } from "@/components/clients/ClientUtils";
import { BlockClientDialog } from "@/components/clients/BlockClientDialog";
import { AddClientDialog } from "@/components/clients/AddClientDialog";
import { useNavigate } from "react-router-dom";
import { formatDate } from "@/components/clients/ClientUtils";
import { useClientsSupabaseStore } from "@/store/api/clientsSupabaseStore";
import { isSubscriptionExpired } from "@/components/subscriptions/SubscriptionFormSchema";
import { Client, ClientStatus } from "@/types/types";
import { useIsMobile } from "@/hooks/use-mobile";
import { getStatusText } from "@/utils/clientUtils";
import { AlertDialogHeader, AlertDialogFooter } from "@/components/ui/alert-dialog";
import {
  AlertDialog, AlertDialogContent, AlertDialogTitle, AlertDialogDescription,
  AlertDialogCancel, AlertDialogAction
} from "@/components/ui/alert-dialog";
import { deleteUser } from "@/services/userProfileService";
import { useToast } from "@/components/ui/use-toast";
import { Progress } from "@/components/ui/progress";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { cn } from "@/lib/utils";
import { notificationStore } from "@/store/api/notificationStore";
import { useClientStats } from "@/hooks/useClientStats";
import { Subscription } from "@/types/subscription";
import { useUserStore } from "@/store/userStore";
import { useDraftResumeToast } from "@/hooks/useDraftResumeToast";
import { AttendancePagination } from "@/components/attendance/AttendancePagination";

/* ===================== Helpers i18n & domaine ===================== */


const isExpiringSoon = (expiryDate?: string) => {
  if (!expiryDate) return false;
  const today = new Date();
  const expiry = new Date(expiryDate);
  const diff = (expiry.getTime() - today.getTime()) / (1000 * 3600 * 24);
  return diff > 0 && diff <= 14;
};

const daysLeftRaw = (expiryDate?: string) => {
  if (!expiryDate) return null;
  const today = new Date();
  const expiry = new Date(expiryDate);
  return Math.ceil((expiry.getTime() - today.getTime()) / (1000 * 3600 * 24));
};
const daysLeftNonNegative = (expiryDate?: string) => {
  const d = daysLeftRaw(expiryDate);
  if (d === null) return null;
  return Math.max(0, d);
};

const usagePct = (total?: number, remaining?: number) => {
  const t = total ?? 0;
  const r = remaining ?? 0;
  if (t <= 0) return 0;
  const used = Math.max(0, t - r);
  return Math.min(100, Math.round((used / t) * 100));
};

const renewalCount = (history?: Subscription[]) => Array.isArray(history) ? history.length : 0;

const TWO_WEEKS_MS = 14 * 24 * 3600 * 1000;
const isClientActive = (c: Client) => {
  // 1) Statut d'abonnement strictement "active"
  const status = String(c.status ?? "").toLowerCase();
  if (status !== "active") return false;

  // 2) Doit avoir un abonnement
  const hasSubscription = !!c.expiryDate || !!c.membershipType;
  if (!hasSubscription) return false;

  // 3) Exclure dates dépassées et packs à 0 sessions
  const isDateExpired = !!c.expiryDate && isSubscriptionExpired(c.expiryDate);
  if (isDateExpired) return false;

  const isSessionsDepleted = (c.sessionsTotal ?? 0) > 0 && (c.sessionsRemaining ?? 0) <= 0;
  if (isSessionsDepleted) return false;

  return true;
};
const hasNoRecentBooking = (c: Client) => {
  if (!c.lastBookingAt) return true;
  return Date.now() - new Date(c.lastBookingAt).getTime() > TWO_WEEKS_MS;
};

const churnRiskScore = (c: Client) => {
  let score = 0;
  const dLeft = daysLeftRaw(c.expiryDate || undefined);
  const pct = usagePct(c.sessionsTotal, c.sessionsRemaining);
  const renews = renewalCount(c.subscriptionHistory);

  if (dLeft !== null) {
    if (dLeft <= 0) score += 40;
    else if (dLeft <= 14) score += 25;
    else if (dLeft <= 30) score += 10;
  }
  if ((c.sessionsTotal ?? 0) > 0 && (c.sessionsRemaining ?? 0) <= 0) score += 30;
  if ((c.sessionsTotal ?? 0) > 0 && pct < 30 && (dLeft ?? 999) <= 14) score += 20;
  if (renews >= 3) score -= 15;

  // `lastVisit` n'est jamais renseigné sur la liste (le serveur ne renvoie
  // aucune date de visite) : ce bloc ne s'exécutait donc jamais et le risque
  // était sous-évalué de 15 à 25 points. On retombe sur la dernière
  // réservation, seul signal d'activité réellement disponible.
  const lastActivityAt = c.lastVisit || c.lastBookingAt;
  if (lastActivityAt) {
    const lv = new Date(lastActivityAt);
    if (!Number.isNaN(lv.getTime())) {
      const diffDays = Math.ceil((Date.now() - lv.getTime()) / (1000 * 3600 * 24));
      if (diffDays > 30) score += 15;
      if (diffDays > 60) score += 10;
    }
  }
  return Math.max(0, Math.min(100, score));
};
const riskLabelKey = (s: number) => (s >= 70 ? "clients:riskHigh" : s >= 40 ? "clients:riskMedium" : "clients:riskLow");
const statusPriority = (status: ClientStatus) =>
  status === "Expired" ? 1 : status === "Blocked" ? 2 : status === "Inactive" ? 3 : status === "Active" ? 4 : 5;

/* ===================== Export CSV ===================== */
const exportToCSV = (rows: Client[]) => {
  const headers = [
    "id","firstName","lastName","email","phone","membershipType","status","startDate","expiryDate",
    "sessionsRemaining","sessionsTotal","jokersRemaining","jokersTotal","renewalCount","churnRisk"
  ];
  const body = rows.map(c => [
    c.id,c.firstName,c.lastName,c.email,c.phone,c.membershipType,c.status,c.startDate,c.expiryDate,
    c.sessionsRemaining,c.sessionsTotal,c.jokersRemaining,c.jokersTotal,
    renewalCount(c.subscriptionHistory),
    churnRiskScore(c)
  ]);
  const csv = [headers, ...body]
    .map(r => r.map(v => `"${String(v ?? "").replace(/"/g, '""')}"`).join(","))
    .join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `clients_export_${new Date().toISOString().slice(0,10)}.csv`;
  a.click();
  URL.revokeObjectURL(url);
};

/* ===================== Mini charts (inchangés, fluides) ===================== */
function HeatmapMini({
  buckets,
  title,
  onBucketClick,
  selectedIndex,
}: {
  buckets: { label: string; value: number }[];
  title?: string;
  onBucketClick?: (index: number) => void;
  selectedIndex?: number | null;
}) {
  const max = Math.max(1, ...buckets.map(b => b.value));
  return (
    <div className="rounded-xl border p-3">
      {title && <div className="text-xs text-muted-foreground mb-2">{title}</div>}
      <div className="grid grid-cols-4 sm:grid-cols-8 gap-2">
        {buckets.map((b, i) => {
          const intensity = 0.2 + 0.8 * (b.value / max);
          const isSelected = selectedIndex === i;
          const clickable = !!onBucketClick;
          const Tag: any = clickable ? "button" : "div";
          return (
            <Tag
              key={i}
              type={clickable ? "button" : undefined}
              onClick={clickable ? () => onBucketClick!(i) : undefined}
              className={cn(
                "rounded-lg border p-3 text-center transition",
                clickable && "cursor-pointer hover:border-primary hover:shadow-sm",
                isSelected && "ring-2 ring-primary border-primary"
              )}
            >
              <div className="h-10 rounded-md" style={{ backgroundColor: `rgba(0,0,0,${intensity})` }} />
              <div className="mt-1 text-xs text-muted-foreground">{b.label}</div>
              <div className="text-sm font-medium">{b.value}</div>
            </Tag>
          );
        })}
      </div>
    </div>
  );
}

/* ===================== UI Mobile ===================== */
function RiskBadge({ score }: { score: number }) {
  const { t } = useTranslation(["clients", "common"]);
  const label = t(riskLabelKey(score));
  const tone = score >= 70 ? "danger" : score >= 40 ? "warn" : "ok";
  const cls = tone === "danger" ? "bg-red-100 text-red-800"
    : tone === "warn" ? "bg-yellow-100 text-yellow-800"
    : "bg-emerald-100 text-emerald-800";
  return <span className={cn("px-2 py-0.5 rounded text-[11px] font-medium", cls)}>{label} {score}</span>;
}

function MobileClientCard({
  client, selected, onToggle, onView, onBlockToggle, onRenew, onDelete, t
}: {
  client: Client;
  selected: boolean;
  onToggle: (checked: boolean) => void;
  onView: () => void;
  onBlockToggle: () => void;
  onRenew: () => void;
  onDelete: () => void;
  t: any;
}) {
  const dLeftNN = daysLeftNonNegative(client.expiryDate || undefined);
  const pct = usagePct(client.sessionsTotal, client.sessionsRemaining);
  const showSoon = isExpiringSoon(client.expiryDate) && !["Blocked","Expired","Inactive"].includes(client.status);
  const renews = renewalCount(client.subscriptionHistory);
  const risk = churnRiskScore(client);
  return (
    <div className={cn(
      "rounded-2xl border p-3 flex flex-col gap-3",
      client.status === "Expired" ? "bg-red-50/50" : showSoon ? "bg-yellow-50/50" : "bg-card"
    )}>
      <div className="flex items-start gap-3">
        <input
          type="checkbox"
          className="mt-1"
          checked={selected}
          onChange={(e) => onToggle(e.currentTarget.checked)}
          aria-label={`select ${client.firstName} ${client.lastName}`}
        />
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-2">
            <div className="font-semibold truncate">{client.firstName} {client.lastName}</div>
            <Badge className={cn("shrink-0", getStatusBadgeStyle(client.status || "Active"))}>
              {t(getStatusText(client.status), { ns: "subscriptions" })}
            </Badge>
          </div>
          {client.lastVisit && (
            <div className="text-[11px] text-muted-foreground">
              {t("clients:lastVisit","Dernière visite")}: {formatDate(client.lastVisit)}
            </div>
          )}
          <div className="text-[11px] text-muted-foreground">
            {t("clients:lastBooking","Dernière réservation")}: {client.lastBookingAt ? formatDate(client.lastBookingAt) : t("clients:neverBooked","Jamais")}
          </div>
        </div>
      </div>


      <div className="grid grid-cols-2 gap-2 text-xs">
        <div className="rounded-lg border p-2">
          <div className="text-muted-foreground">{t("endDate", { ns: "subscriptions" })}</div>
          <div className="font-medium">{client.expiryDate ? formatDate(client.expiryDate) : "-"}</div>
        </div>
        <div className="rounded-lg border p-2">
          <div className="text-muted-foreground">{t("clients:daysLeft","Jours restants")}</div>
          <div className={cn("font-medium", dLeftNN === 0 ? "text-red-600" : "")}>{dLeftNN === null ? "-" : dLeftNN}</div>
        </div>
        <div className="rounded-lg border p-2">
          <div className="text-muted-foreground">{t("type", { ns: "subscriptions" })}</div>
          <div className="font-medium truncate">{client.membershipType ? t(`subscriptions:${client.membershipType}`, client.membershipType) : "-"}</div>
        </div>
        <div className="rounded-lg border p-2">
          <div className="text-muted-foreground">{t("clients:renewals","#Renouvel.")}</div>
          <div className="font-medium">{renews}</div>
        </div>
      </div>

      <div>
        <div className="flex items-center justify-between text-xs mb-1">
          <div>{t("clients:usage","Utilisation")}</div>
          <div className="font-medium">{client.sessionsRemaining}/{client.sessionsTotal || 0}</div>
        </div>
        <Progress value={pct} />
      </div>

      <div className="flex items-center justify-between">
        <RiskBadge score={risk} />
        <div className="flex items-center gap-1">
          <Button size="xs" variant="secondary" onClick={onView}><Eye className="h-3.5 w-3.5 mr-1" /> {t("common:view")}</Button>
          {(client.status === "Expired" || showSoon) && (
            <Button size="xs" onClick={onRenew}><RenewIcon className="h-3.5 w-3.5 mr-1" /> {t("clients:renew","Renouveler")}</Button>
          )}
          <Button size="icon" variant="ghost" onClick={onBlockToggle} aria-label="block/unblock">
            <ShieldBan className={cn("h-4 w-4", client.status === "Blocked" ? "text-emerald-600" : "text-red-600")} />
          </Button>
          <Button size="icon" variant="ghost" onClick={onDelete} aria-label="delete">
            <Trash2 className="h-4 w-4 text-red-500" />
          </Button>
        </div>
      </div>
    </div>
  );
}

function MobileBulkBar({
  count, onReminder, onExport, onClear
}: {
  count: number; onReminder: () => void; onExport: () => void; onClear: () => void;
}) {
  const { t } = useTranslation(["clients", "subscriptions", "common"]);
  return (
    <div className="fixed bottom-0 left-0 right-0 z-20 border-t bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
      <div className="mx-auto max-w-screen-lg p-2 flex items-center gap-2">
        <div className="text-xs text-muted-foreground">{count} {t("clients:selected","sélectionné(s)")}</div>
        <div className="ml-auto flex items-center gap-1">
          <Button size="sm" variant="outline" onClick={onReminder}><Mail className="h-4 w-4 mr-1" />{t("clients:sendReminder","Rappel")}</Button>
          <Button size="sm" variant="outline" onClick={onExport}><Download className="h-4 w-4 mr-1" />{t("clients:exportSelection","Exporter")}</Button>
          <Button size="sm" variant="ghost" onClick={onClear}><RefreshCw className="h-4 w-4 mr-1" />{t("clients:clearSelection","Vider")}</Button>
        </div>
      </div>
    </div>
  );
}

/* ===================== Composant principal ===================== */
export default function Clients() {
  const { t } = useTranslation(["clients", "subscriptions", "common"]);
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const { toast } = useToast();

  const [searchQuery, setSearchQuery] = useState("");
  const [sortBy, setSortBy] = useState<string>("firstName");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("asc");
  const [isBlockDialogOpen, setIsBlockDialogOpen] = useState(false);
  const [selectedClient, setSelectedClient] = useState<Client | null>(null);
  const [isAddClientDialogOpen, setIsAddClientDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [clientToDelete, setClientToDelete] = useState<Client | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string | number>>(new Set());
  const [quickFilter, setQuickFilter] = useState<
    "all" | "active" | "expiring" | "expired" | "expiredWithSessions" | "expiredNoSessions" | "inactive" | "blocked" | "paused" | "expiringWeek" | "noRecentBooking"
  >("all");
  const [selectedWeekIndex, setSelectedWeekIndex] = useState<number | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 25;

  const clientsStore = useClientsSupabaseStore();
  const { gymId } = useUserStore();

  const { clients, kpis } = useClientStats();

  useEffect(() => {
    if (gymId) {
      clientsStore.fetchClients();
    }
  }, [gymId]);

  // Si une saisie client a été interrompue par un reload (mise à jour, kill
  // du process en background), proposer de la reprendre — sans rouvrir le
  // dialog d'autorité. Le formulaire se restaure seul à l'ouverture.
  useDraftResumeToast([
    {
      formId: "add-client",
      message: t("clients:draftUnsaved", "Saisie client non enregistrée"),
      description: (d) =>
        [d.firstName, d.lastName].filter(Boolean).join(" ") || (d.email as string) || undefined,
      isEmpty: (d) => !(d.firstName || d.lastName || d.email || d.phone),
      onResume: () => setIsAddClientDialogOpen(true),
    },
  ]);

  const next8WeeksHeatmap = useMemo(() => {
    // `now` était construit dans le corps du composant et passé en dépendance :
    // `new Date()` produit une référence neuve à chaque rendu, donc le `useMemo`
    // se recalculait systématiquement (mémoïsation annulée, audit lot 7).
    // La date de calcul est interne aux seaux : on la construit ici.
    const now = new Date();
    const buckets: { label: string; value: number; start: Date; end: Date }[] = [];
    for (let i = 0; i < 8; i++) {
      const start = new Date(now);
      start.setDate(now.getDate() + i * 7);
      start.setHours(0, 0, 0, 0);
      const end = new Date(start);
      end.setDate(start.getDate() + 6);
      end.setHours(23, 59, 59, 999);
      const label = `${start.getDate()}/${start.getMonth()+1} - ${end.getDate()}/${end.getMonth()+1}`;
      const value = clients.filter(c => {
        if (!c.expiryDate) return false;
        const e = new Date(c.expiryDate);
        return e >= start && e <= end;
      }).length;
      buckets.push({ label, value, start, end });
    }
    return buckets;
  }, [clients]);

  const noRecentBookingCount = useMemo(
    () => clients.filter(c => isClientActive(c) && hasNoRecentBooking(c)).length,
    [clients]
  );


  /* ===== Recherche + filtres ===== */
  const filtered = useMemo(() => {
    const q = searchQuery.toLowerCase();
    let arr = clients.filter(client => {
      const fullName = `${client.firstName} ${client.lastName}`.toLowerCase();
      const membership = client.membershipType?.toLowerCase() || "";
      const statusText = t(getStatusText(client.status), { ns: "subscriptions" }).toLowerCase();
      return (
        fullName.includes(q) ||
        (client.email && client.email.toLowerCase().includes(q)) ||
        (client.phone && client.phone.toLowerCase().includes(q)) ||
        membership.includes(q) ||
        statusText.includes(q)
      );
    });

    if (quickFilter && quickFilter !== "all") {
      arr = arr.filter(c => {
        if (quickFilter === "active") {
          const isDateExpired = !!c.expiryDate && isSubscriptionExpired(c.expiryDate);
          const isSessionsDepleted = (c.sessionsTotal ?? 0) > 0 && (c.sessionsRemaining ?? 0) <= 0;
          return !isDateExpired && !isSessionsDepleted && c.status !== "Blocked" && c.status !== "Inactive";
        }
        if (quickFilter === "blocked") return c.status === "Blocked";
        if (quickFilter === "paused") return c.status === "Paused";
        if (quickFilter === "inactive") return c.status === "Inactive";
        if (quickFilter === "expired") return (c.status === "Expired"
             || (!!c.expiryDate && isSubscriptionExpired(c.expiryDate))
             || ((c.sessionsTotal ?? 0) > 0 && (c.sessionsRemaining ?? 0) <= 0))&& c.status !== "Paused";
        if (quickFilter === "expiredWithSessions")
          return (c.status === "Expired" || (!!c.expiryDate && isSubscriptionExpired(c.expiryDate)))
        && c.status !== "Paused" &&
                 (c.sessionsTotal ?? 0) > 0 && (c.sessionsRemaining ?? 0) > 0;
        if (quickFilter === "expiredNoSessions")
          return (c.status === "Expired" || (!!c.expiryDate && isSubscriptionExpired(c.expiryDate))) && c.status !== "Paused"
         &&     (c.sessionsTotal ?? 0) > 0 && (c.sessionsRemaining ?? 0) === 0;
        if (quickFilter === "expiring")
          return (
            (isExpiringSoon(c.expiryDate) && !["Blocked","Inactive"].includes(c.status) && !(!!c.expiryDate && isSubscriptionExpired(c.expiryDate))) ||
            (((c.sessionsTotal ?? 0) > 0) && (c.sessionsRemaining ?? 0) <= 2 && !((c.sessionsTotal ?? 0) > 0 && (c.sessionsRemaining ?? 0) <= 0))
          ) && c.status !== "Paused";
        if (quickFilter === "expiringWeek") {
          if (selectedWeekIndex === null) return false;
          const bucket = next8WeeksHeatmap[selectedWeekIndex];
          if (!bucket || !c.expiryDate) return false;
          const e = new Date(c.expiryDate);
          return e >= bucket.start && e <= bucket.end;
        }
        if (quickFilter === "noRecentBooking") {
          return isClientActive(c) && hasNoRecentBooking(c);
        }
        return true;
      });
    }
    return arr;
  }, [clients, searchQuery, quickFilter, selectedWeekIndex, next8WeeksHeatmap, t]);

  /* ===== Tri ===== */
  const sortedClients = useMemo(() => {
    const arr = [...filtered];
    if (!sortBy) return arr;

    const order = sortOrder === "asc" ? 1 : -1;
    const byNum = (a: number, b: number) => (a - b) * order;

    return arr.sort((a, b) => {
      switch (sortBy) {
        case "status": return byNum(statusPriority(a.status), statusPriority(b.status));
        case "renewals": return byNum(renewalCount(a.subscriptionHistory), renewalCount(b.subscriptionHistory));
        case "risk": return byNum(churnRiskScore(a), churnRiskScore(b));
        case "daysLeft": {
          const av = (daysLeftNonNegative(a.expiryDate || undefined) ?? 9999);
          const bv = (daysLeftNonNegative(b.expiryDate || undefined) ?? 9999);
          return byNum(av, bv);
        }
        case "usage": return byNum(usagePct(a.sessionsTotal, a.sessionsRemaining), usagePct(b.sessionsTotal, b.sessionsRemaining));
        default: {
          let av: any = a[sortBy as keyof Client] ?? "";
          let bv: any = b[sortBy as keyof Client] ?? "";
          if (["startDate","expiryDate","lastVisit","birthDate"].includes(sortBy)) {
            av = av ? new Date(av).getTime() : 0;
            bv = bv ? new Date(bv).getTime() : 0;
            return byNum(av, bv);
          }
          return (("" + av).localeCompare("" + bv, undefined, { sensitivity: "base" })) * order;
        }
      }
    });
  }, [filtered, sortBy, sortOrder]);

  // Reset page when search/filter changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, quickFilter, selectedWeekIndex]);

  // Paginated slice
  const paginatedClients = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return sortedClients.slice(start, start + pageSize);
  }, [sortedClients, currentPage, pageSize]);

  /* ===== Handlers ===== */
  const handleSort = (col: string) =>
    setSortBy(prev => (prev === col ? (setSortOrder(o => (o === "asc" ? "desc" : "asc")), prev) : (setSortOrder("asc"), col)));
  const handleViewClient = (id: string | number) => navigate(`/admin/clients/${String(id)}`);
  const handleBlockClient = (c: Client) => { setSelectedClient(c); setIsBlockDialogOpen(true); };
  const handleConfirmDeleteClient = (c: Client) => { setClientToDelete(c); setIsDeleteDialogOpen(true); };

  const confirmDeleteClient = async () => {
    if (!clientToDelete) return;
    setIsDeleting(true);
    try {
      const result = await deleteUser(clientToDelete.id);
      if (result.success) {
        const clientName = `${clientToDelete.firstName} ${clientToDelete.lastName}`;
        if (result.deletionType === 'partial') {
          // Le compte auth existe toujours : l'adresse e-mail reste prise, on
          // le dit plutôt que d'annoncer une suppression.
          toast({
            title: t("success", { ns: "common" }),
            description: result.scopeUnknown
              ? t("clients:clientRemovedFromGymUnknownScope", {
                  name: clientName,
                  defaultValue:
                    "{{name}} a été retiré de cette salle. Le nombre de salles n'a pas pu être vérifié : le compte et son adresse e-mail sont conservés.",
                })
              : t("clients:clientRemovedFromGym", { name: clientName, remainingGyms: result.remainingGyms }),
          });
        } else if (result.authDeleted === false) {
          // Plan global demandé mais compte auth non supprimé côté serveur :
          // l'e-mail restera pris, la recréation échouera. Pas de faux succès.
          toast({
            title: t("common:warning"),
            description: t("clients:clientDeletedAuthKept", { name: clientName }),
            variant: "destructive",
          });
        } else {
          toast({ title: t("success", { ns: "common" }), description: t("clients:clientDeleted", { name: clientName }) });
        }
        clientsStore.fetchClients();
      } else {
        toast({ title: t("common:error"), description: t("clients:deleteError", { name: `${clientToDelete.firstName} ${clientToDelete.lastName}` }), variant: "destructive" });
      }
    } catch (e) {
      console.error("Error deleting client:", e);
      toast({ title: t("common:error"), description: t("clients:deleteError", { name: `${clientToDelete.firstName} ${clientToDelete.lastName}` }), variant: "destructive" });
    } finally {
      setIsDeleting(false); setIsDeleteDialogOpen(false); setClientToDelete(null);
    }
  };

  const handleClientAdded = () => clientsStore.fetchClients();

  // sélection
  const headerCheckboxRef = useRef<HTMLInputElement | null>(null);
  const toggleRow = (id: string | number, checked: boolean) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (checked) next.add(id); else next.delete(id);
      return next;
    });
  };
  const toggleAll = (checked: boolean) => setSelectedIds(checked ? new Set(paginatedClients.map(c => c.id)) : new Set());
  const allChecked = paginatedClients.length > 0 && paginatedClients.every(c => selectedIds.has(c.id));

  // Actions de masse (stubs)
  // Remplacer l’ancienne version
const sendReminder = () => {
  if (selectedIds.size === 0) {
    toast({
      title: t("clients:noSelection"),
      description: t("clients:selectUsersFirst"),
      variant: "destructive",
    });
    return;
  }

  void (async () => {
    const ids = Array.from(selectedIds).map(String);

    const res = await notificationStore.createAdminMessages(ids, {
      text: t("adminui3:clientsList.reminderText"),
      title: t("adminui3:clientsList.reminderTitle")
      // scheduledAt: new Date(), // facultatif, par défaut: maintenant
      // extraPayload: { source: "clients_list" } // si tu veux enrichir
    });

    if (!res.ok) {
      console.error("[notifications_plan.insert] error:", res.error);
      toast({
        title: t("common:error"),
        description: t("clients:remindersError", "Impossible de créer les notifications."),
        variant: "destructive",
      });
      return;
    }

    toast({
      title: t("clients:remindersQueued", "Rappels préparés"),
      description: t("clients:remindersCreated", "{{count}} notification(s) créée(s).", { count: res.inserted }),
    });
  })();
};


  /* ===================== RENDER ===================== */
  return (
    <div className="container mx-auto p-2 sm:p-6 pb-20 sm:pb-6">
      {/* Header + actions */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-4">
        <h1 className={`${isMobile ? "text-lg" : "text-2xl"} font-bold`}>{t("title")}</h1>
        <div className="flex items-center gap-2 w-full sm:w-auto">
          <Button className="hidden sm:inline-flex w-full sm:w-auto" variant="outline" size="sm" onClick={() => exportToCSV(sortedClients)}>
            <Download className="h-4 w-4 mr-2" /> {t("clients:exportCsv","Exporter CSV")}
          </Button>
          <Button className="hidden sm:inline-flex w-full sm:w-auto" variant="outline" size="sm" onClick={() => navigate('/admin/clients/import')}>
            <Upload className="h-4 w-4 mr-2" /> {t("clients:import.title","Import CSV")}
          </Button>
          <Button className="w-full sm:w-auto" onClick={() => setIsAddClientDialogOpen(true)} size={isMobile ? "sm" : "sm"}>
            <PlusCircle className="h-4 w-4 mr-2" /> {t("addClient")}
          </Button>
        </div>
      </div>

      {/* KPI grid : compacte sur mobile */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-7 gap-2 sm:gap-3 mb-4">
        <KPI icon={<Activity className="h-3 w-3" />} label={t("clients:total","Total")} value={kpis.total} help={t("clients:totalHelp","Comptes")} onClick={() => setQuickFilter("all")} />
        <KPI icon={<TrendingUp className="h-3 w-3" />} label={t("subscriptions:active")} value={kpis.active} help={t("adminui3:clientsList.pausedSuffix", { count: kpis.totalPaused })} tone="ok" onClick={() => setQuickFilter("active")} />
        <KPI icon={<CalendarClock className="h-3 w-3" />} label={t("clients:clientsExpiringSoon","Expirent bientôt")} value={kpis.expSoon} tone="warn" onClick={() => setQuickFilter("expiring")} />
        <KPI icon={<AlertCircle className="h-3 w-3" />} label={t("clients:clientsExpired","Expirés")} value={kpis.expired} tone="danger" onClick={() => setQuickFilter("expired")} />
        <KPI icon={<AlertCircle className="h-3 w-3" />} label={t("clients:clientsExpiredWithSessions","Expirés (date de fin)")} value={kpis.expiredWithSessions} tone="warn" onClick={() => setQuickFilter("expiredWithSessions")} />
        <KPI icon={<AlertCircle className="h-3 w-3" />} label={t("clients:expiredNoSessions","Expirés (0 session)")} value={kpis.expiredNoSessions} tone="danger" onClick={() => setQuickFilter("expiredNoSessions")} />
        <KPI icon={<Ban className="h-3 w-3" />} label={t("subscriptions:Bloqués")} value={kpis.blocked} tone="muted" onClick={() => setQuickFilter("blocked")} />
        <KPI icon={<AlertCircle className="h-3 w-3" />} label={t("clients:paused","En pause")} value={kpis.totalPaused} tone="danger" onClick={() => setQuickFilter("paused")} />
        <KPI icon={<CalendarClock className="h-3 w-3" />} label={t("clients:noRecentBooking","Sans réservation +2 sem.")} value={noRecentBookingCount} tone="warn" onClick={() => setQuickFilter("noRecentBooking")} />

      </div>


      {/* Charts : allégés sur mobile (1 chart), complets sur desktop */}
        <div className="mb-4">
          <HeatmapMini
            title={t("clients:expirationsNext","Expirations - 8 semaines")}
            buckets={next8WeeksHeatmap}
            selectedIndex={quickFilter === "expiringWeek" ? selectedWeekIndex : null}
            onBucketClick={(i) => {
              if (quickFilter === "expiringWeek" && selectedWeekIndex === i) {
                setQuickFilter("all");
                setSelectedWeekIndex(null);
              } else {
                setQuickFilter("expiringWeek");
                setSelectedWeekIndex(i);
              }
              setCurrentPage(1);
            }}
          />
          {quickFilter === "expiringWeek" && selectedWeekIndex !== null && next8WeeksHeatmap[selectedWeekIndex] && (
            <div className="mt-2 flex items-center gap-2 text-xs">
              <Badge variant="secondary">
                {t("clients:weekSelected","Semaine sélectionnée")}: {next8WeeksHeatmap[selectedWeekIndex].label} ({next8WeeksHeatmap[selectedWeekIndex].value})
              </Badge>
              <Button
                size="xs"
                variant="ghost"
                onClick={() => {
                  setQuickFilter("all");
                  setSelectedWeekIndex(null);
                }}
              >
                {t("common:clear","Effacer")}
              </Button>
            </div>
          )}
        </div>
      

      {/* Barre de recherche + onglets : scroll horizontale sur mobile */}
      <div className="relative z-10 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-3">
        <div className="relative w-full">
          <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder={t("clients:searchPlaceholder")}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-8"
          />
        </div>
        <div className="w-full sm:w-auto overflow-x-auto [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
          <Tabs value={quickFilter} onValueChange={(v) => setQuickFilter(v as any)}>
            <TabsList className="inline-flex min-w-max gap-1">
              <TabsTrigger className="px-2 py-1 text-xs sm:text-sm" value="all">{t("clients:all","Tous")}</TabsTrigger>
              <TabsTrigger className="px-2 py-1 text-xs sm:text-sm" value="active">{t("subscriptions:active")}</TabsTrigger>
              <TabsTrigger className="px-2 py-1 text-xs sm:text-sm" value="expiring">{t("clients:clientsExpiringSoon","Expirent bientôt")}</TabsTrigger>
              <TabsTrigger className="px-2 py-1 text-xs sm:text-sm" value="expired">{t("clients:clientsExpired","Expirés")}</TabsTrigger>
              <TabsTrigger className="px-2 py-1 text-xs sm:text-sm" value="expiredWithSessions">{t("clients:clientsExpiredWithSessions","Expirés (sessions)")}</TabsTrigger>
              <TabsTrigger className="px-2 py-1 text-xs sm:text-sm" value="expiredNoSessions">{t("clients:expiredNoSessions","Expirés (0 session)")}</TabsTrigger>
              <TabsTrigger className="px-2 py-1 text-xs sm:text-sm" value="inactive">{t("subscriptions:Inactive")}</TabsTrigger>
              <TabsTrigger className="px-2 py-1 text-xs sm:text-sm" value="blocked">{t("subscriptions:Blocked")}</TabsTrigger>
              <TabsTrigger className="px-2 py-1 text-xs sm:text-sm" value="noRecentBooking">{t("clients:noRecentBookingShort","Sans résa +2 sem.")}</TabsTrigger>
            </TabsList>

          </Tabs>
        </div>
      </div>

      {/* Actions de masse : en ligne sur desktop, en barre fixe en bas sur mobile */}
      {!isMobile && selectedIds.size > 0 && (
        <div className="mb-3 flex items-center gap-2 text-sm">
          <div className="text-muted-foreground">{selectedIds.size} {t("clients:selected","sélectionné(s)")}</div>
          <Button variant="outline" size="sm" onClick={sendReminder}>
            <Mail className="h-4 w-4 mr-2" /> {t("clients:sendReminder","Envoyer rappel")}
          </Button>
          <Button variant="outline" size="sm" onClick={() => exportToCSV(sortedClients.filter(c => selectedIds.has(c.id)))}>
            <Download className="h-4 w-4 mr-2" /> {t("clients:exportSelection","Exporter sélection")}
          </Button>
          <Button variant="ghost" size="sm" onClick={() => setSelectedIds(new Set())}>
            <RefreshCw className="h-4 w-4 mr-2" /> {t("clients:clearSelection","Vider sélection")}
          </Button>
        </div>
      )}

      {/* === Rendu MOBILE : cartes === */}
      {isMobile ? (
        <div className="grid grid-cols-1 gap-2">
          {clientsStore.isLoading && (
            <div className="text-center py-6 text-muted-foreground text-sm">Loading...</div>
          )}
          {!clientsStore.isLoading && sortedClients.length === 0 && (
            <div className="text-center py-6 text-muted-foreground text-sm">{t("noClientsFound")}</div>
          )}
          {paginatedClients.map(client => (
            <MobileClientCard
              key={client.id}
              client={client}
              selected={selectedIds.has(client.id)}
              onToggle={(checked) => toggleRow(client.id, checked)}
              onView={() => handleViewClient(client.id)}
              onBlockToggle={() => handleBlockClient(client)}
              onRenew={() => navigate(`/admin/clients/${String(client.id)}?action=renew`)}
              onDelete={() => handleConfirmDeleteClient(client)}
              t={t}
            />
          ))}
        </div>
      ) : (
      /* === Rendu DESKTOP : table === */
        <div className="overflow-x-auto">
          <Table>
            <TableHeader className="sticky top-0 bg-background z-10">
              <TableRow>
                <TableHead className="w-10">
                  <input
                    type="checkbox"
                    aria-label="toggle all"
                    onChange={(e) => toggleAll(e.currentTarget.checked)}
                    checked={allChecked}
                    ref={(el) => {
                      headerCheckboxRef.current = el;
                      if (el) el.indeterminate = selectedIds.size > 0 && !allChecked;
                    }}
                  />
                </TableHead>
                <TableHead onClick={() => handleSort("firstName")} className="cursor-pointer">
                  {t("name")}{sortBy === "firstName" ? (sortOrder === "asc" ? " ▲" : " ▼") : ""}
                </TableHead>
                <TableHead onClick={() => handleSort("email")} className="cursor-pointer">
                  {t("email")}{sortBy === "email" ? (sortOrder === "asc" ? " ▲" : " ▼") : ""}
                </TableHead>
                <TableHead onClick={() => handleSort("phone")} className="cursor-pointer">
                  {t("phone")}{sortBy === "phone" ? (sortOrder === "asc" ? " ▲" : " ▼") : ""}
                </TableHead>
                <TableHead onClick={() => handleSort("startDate")} className="cursor-pointer">
                  {t("startDate", { ns: "subscriptions" })}{sortBy === "startDate" ? (sortOrder === "asc" ? " ▲" : " ▼") : ""}
                </TableHead>
                <TableHead onClick={() => handleSort("expiryDate")} className="cursor-pointer">
                  {t("endDate", { ns: "subscriptions" })}{sortBy === "expiryDate" ? (sortOrder === "asc" ? " ▲" : " ▼") : ""}
                </TableHead>
                <TableHead onClick={() => handleSort("daysLeft")} className="cursor-pointer">
                  {t("clients:daysLeft","Jours restants")}{sortBy === "daysLeft" ? (sortOrder === "asc" ? " ▲" : " ▼") : ""}
                </TableHead>
                <TableHead onClick={() => handleSort("membershipType")} className="cursor-pointer">
                  {t("type", { ns: "subscriptions" })}{sortBy === "membershipType" ? (sortOrder === "asc" ? " ▲" : " ▼") : ""}
                </TableHead>
                <TableHead onClick={() => handleSort("usage")} className="cursor-pointer">
                  {t("clients:usage","Utilisation")}{sortBy === "usage" ? (sortOrder === "asc" ? " ▲" : " ▼") : ""}
                </TableHead>
                <TableHead onClick={() => handleSort("renewals")} className="cursor-pointer">
                  {t("clients:renewals","#Renouvel.")}{sortBy === "renewals" ? (sortOrder === "asc" ? " ▲" : " ▼") : ""}
                </TableHead>
                <TableHead onClick={() => handleSort("risk")} className="cursor-pointer">
                  {t("clients:churnRisk","Risque")}{sortBy === "risk" ? (sortOrder === "asc" ? " ▲" : " ▼") : ""}
                </TableHead>
                <TableHead onClick={() => handleSort("status")} className="cursor-pointer">
                  {t("status", { ns: "subscriptions" })}{sortBy === "status" ? (sortOrder === "asc" ? " ▲" : " ▼") : ""}
                </TableHead>
                <TableHead className="text-right">{t("common:actions")}</TableHead>
              </TableRow>
            </TableHeader>

            <TableBody>
              {paginatedClients.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={13} className="text-center py-6 text-muted-foreground">
                    {clientsStore.isLoading ? "Loading..." : t("noClientsFound")}
                  </TableCell>
                </TableRow>
              ) : (
                paginatedClients.map((client) => {
                  const dLeftNN = daysLeftNonNegative(client.expiryDate || undefined);
                  const pct = usagePct(client.sessionsTotal, client.sessionsRemaining);
                  const showSoon = isExpiringSoon(client.expiryDate) && !["Blocked","Expired","Inactive"].includes(client.status);
                  const renews = renewalCount(client.subscriptionHistory);
                  const risk = churnRiskScore(client);

                  return (
                    <TableRow key={client.id} className={cn(
                      client.status === "Expired" ? "bg-red-50/40" : showSoon ? "bg-yellow-50/40" : ""
                    )}>
                      <TableCell className="align-middle">
                        <input
                          type="checkbox"
                          checked={selectedIds.has(client.id)}
                          onChange={(e) => toggleRow(client.id, e.currentTarget.checked)}
                          aria-label={`select ${client.firstName} ${client.lastName}`}
                        />
                      </TableCell>

                      <TableCell className="text-sm p-3">
                        <div className="font-medium truncate">{`${client.firstName} ${client.lastName}`}</div>
                        <div className="text-xs text-muted-foreground">
                          {client.lastVisit ? `${t("clients:lastVisit","Dernière visite")}: ${formatDate(client.lastVisit)}` : ""}
                        </div>
                      </TableCell>

                      <TableCell className="text-sm p-3">{client.email}</TableCell>
                      <TableCell className="text-sm p-3">{client.phone}</TableCell>
                      <TableCell className="text-sm p-3">{client.startDate ? formatDate(client.startDate) : ""}</TableCell>
                      <TableCell className="text-sm p-3">{client.expiryDate ? formatDate(client.expiryDate) : ""}</TableCell>
                      <TableCell className="text-sm p-3">{dLeftNN === null ? "-" : dLeftNN}</TableCell>
                      <TableCell className="text-sm p-3">{client.membershipType ? t(`subscriptions:${client.membershipType}`, client.membershipType) : ""}</TableCell>
                      <TableCell className="text-sm p-3">
                        <div className="mb-1">{client.sessionsRemaining}/{client.sessionsTotal || 0}</div>
                        <Progress value={pct} />
                      </TableCell>
                      <TableCell className="text-sm p-3">{renews}</TableCell>
                      <TableCell className="text-sm p-3"><RiskBadge score={risk} /></TableCell>
                      <TableCell className="text-sm p-3">
                        <Badge className={getStatusBadgeStyle(client.status || "Active")}>
                          {t(getStatusText(client.status), { ns: "subscriptions" })}
                        </Badge>
                      </TableCell>

                      <TableCell className="text-right p-3">
                        <div className="flex justify-end gap-2">
                          <Button variant="ghost" size="sm" onClick={() => handleViewClient(client.id)}>{t("common:view")}</Button>
                          <Button
                            variant="ghost" size="sm"
                            onClick={() => handleBlockClient(client)}
                            className={client.status === "Blocked" ? "text-green-600" : "text-red-600"}
                          >
                            {client.status === "Blocked" ? t("unblock") : t("block")}
                          </Button>
                          {(client.status === "Expired" || showSoon) && (
                            <Button variant="secondary" size="sm" onClick={() => navigate(`/admin/clients/${String(client.id)}?action=renew`)}>
                              {t("clients:renew","Renouveler")}
                            </Button>
                          )}
                          <Button variant="ghost" size="sm" onClick={() => handleConfirmDeleteClient(client)} className="text-red-500">
                            {t("delete")}
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </div>
      )}

      {/* Pagination */}
      <AttendancePagination
        currentPage={currentPage}
        totalCount={sortedClients.length}
        pageSize={pageSize}
        onPageChange={setCurrentPage}
      />

      {/* Barre fixe mobile si sélection */}
      {isMobile && selectedIds.size > 0 && (
        <MobileBulkBar
          count={selectedIds.size}
          onReminder={sendReminder}
          onExport={() => exportToCSV(sortedClients.filter(c => selectedIds.has(c.id)))}
          onClear={() => setSelectedIds(new Set())}
        />
      )}

      {/* Dialogs */}
      {selectedClient && (
        <BlockClientDialog
          client={selectedClient}
          open={isBlockDialogOpen}
          onOpenChange={setIsBlockDialogOpen}
          isBlocked={selectedClient.status === "Blocked"}
          onBlockedChange={() => clientsStore.fetchClients()}
        />
      )}
      <AddClientDialog open={isAddClientDialogOpen} onOpenChange={setIsAddClientDialogOpen} onClientAdded={handleClientAdded} />

      <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("deleteConfirmationTitle")}</AlertDialogTitle>
            <AlertDialogDescription>
              {t("deleteConfirmationMessage", { name: `${clientToDelete?.firstName} ${clientToDelete?.lastName}` })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setIsDeleteDialogOpen(false)} disabled={isDeleting}>
              {t("common:cancel")}
            </AlertDialogCancel>
            <AlertDialogAction className="bg-red-600 hover:bg-red-700" onClick={confirmDeleteClient} disabled={isDeleting}>
              {isDeleting ? t("common:deleting") : t("common:delete")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

/* ====== KPI (identique, déjà responsive via grid) ====== */
function KPI({
  icon, label, value, help, trend, tone = "default", onClick
}: {
  icon?: React.ReactNode; label: string; value: number; help?: string;
  trend?: {
    value: number;
    isPositive: boolean;
  };
  tone?: "default"|"warn"|"danger"|"muted"|"ok";
  onClick?: () => void;
}) {
  const isMobile = useIsMobile();
  const base = "rounded-xl border p-3 sm:p-4 flex flex-col gap-1 cursor-pointer transition hover:shadow-sm";
  const tones = {
    default: "bg-card",
    warn: "bg-yellow-50 border-yellow-200",
    danger: "bg-red-50 border-red-200",
    muted: "bg-muted/30",
    ok: "bg-emerald-50 border-emerald-200"
  } as const;
  return (
    <div className={cn(base, tones[tone])} onClick={onClick}>
      <div className="flex items-center gap-2 text-xs text-muted-foreground">{icon}{label}</div>
      <div className="text-xl sm:text-2xl font-semibold">{value}</div>
      {help && <div className="text-[11px] text-muted-foreground">{help}</div>}
      {(trend) && (
          <div className="flex items-center mt-0.5 sm:mt-1">
            {trend && (
              <span
                className={cn(
                  "mr-1 font-medium",
                  isMobile ? "text-[9px]" : "text-xs",
                  trend.isPositive
                    ? "text-emerald-500"
                    : "text-rose-500"
                )}
              >
                {trend.isPositive ? "+" : ""}
                {trend.value}%
              </span>
            )}
          </div>
        )}
    </div>
  );
}
